import { Search } from 'lucide-react'
import { lazy, Suspense, useEffect, useState } from 'react'

import type { DownloadJobStatus } from '@/api/types/download-queue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { useDownloadQueueStore } from '@/store/useDownloadQueueStore'

const DownloadQueueTable = lazy(() => import('./download-queue-table'))
const StatusFilter = lazy(() => import('./download-queue-table').then((m) => ({ default: m.StatusFilter })))

export function DownloadQueueTab() {
  const jobs = useDownloadQueueStore((s) => s.jobs)
  const fetchJobs = useDownloadQueueStore((s) => s.fetchJobs)
  const submitUrl = useDownloadQueueStore((s) => s.submitUrl)
  const retryAll = useDownloadQueueStore((s) => s.retryAll)
  const [url, setUrl] = useState('')
  const { isSubmitting: submitting, run } = useSubmitLock()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('dq-auto-refresh') === 'true')
  const [statusFilter, setStatusFilter] = useState<DownloadJobStatus | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const handleAutoRefreshChange = (checked: boolean) => {
    setAutoRefresh(checked)
    localStorage.setItem('dq-auto-refresh', String(checked))
  }

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  useEffect(() => {
    const { startPolling, stopPolling } = useDownloadQueueStore.getState()
    if (autoRefresh) {
      startPolling()
    } else {
      stopPolling()
    }
    return () => useDownloadQueueStore.getState().stopPolling()
  }, [autoRefresh])

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    const trimmed = url.trim()
    if (!trimmed) return

    setSubmitError(null)
    await run(async () => {
      try {
        await submitUrl(trimmed)
        setUrl('')
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'URL送信に失敗しました')
      }
    })
  }

  return (
    <div className="p-4 w-full max-w-5xl mx-auto space-y-4">
      {/* URL送信フォーム */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
        <Input
          type="url"
          placeholder="ダウンロードURLを入力..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="flex-1 min-w-0"
          disabled={submitting}
          required
        />
        <Button type="submit" disabled={submitting || !url.trim()} className="w-full sm:w-auto">
          {submitting ? '送信中...' : '追加'}
        </Button>
      </form>
      {submitError && <p className="text-destructive text-sm">{submitError}</p>}

      {/* 操作ボタン */}
      <div className="space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-2">
        <div className="relative w-full sm:flex-1 sm:max-w-xs">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="URL・タイトル・ドメインで検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Switch checked={autoRefresh} onCheckedChange={handleAutoRefreshChange} />
            自動更新
          </label>
          {!autoRefresh && (
            <Button variant="outline" size="sm" onClick={() => fetchJobs()}>
              更新
            </Button>
          )}
          {jobs.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => retryAll()}>
              一括リトライ
            </Button>
          )}
        </div>
      </div>

      {/* ステータスフィルター & テーブル */}
      <Suspense fallback={<p className="text-muted-foreground text-center py-8">読み込み中...</p>}>
        <StatusFilter jobs={jobs} activeStatus={statusFilter} onToggle={setStatusFilter} />
        <DownloadQueueTable jobs={jobs} statusFilter={statusFilter} searchQuery={searchQuery} />
      </Suspense>
    </div>
  )
}
